import * as React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import { PrimaryButton } from '@fluentui/react';
import { LieferungStatusLabel } from 'utils/labels';
import { formStyles } from 'styles/formStyles';
import { Lieferung } from 'api/lieferung';
import { Timestamps } from 'formComponents/Timestamps';
import { LieferungPosition } from 'api/lieferungPosition';
import {
    betreuerByNrSelector,
    laenderByIkz2Selector,
    skontodefinitionenByArtSelector,
    textbausteineByKzNrSelector,
} from 'store/lookups/selector';
import { formatMoney, formatPercent, formatSmDate } from 'utils/formatter';
import { authSelector } from 'store/auth/selector';
import nl2br from 'react-nl2br';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { LieferungPositionen } from './LieferungPositionen';

const styles = mergeStyleSets({
    shortLine: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '120px 120px 1fr 100px max-content',
        gridColumnGap: 10,
    },
    status: {
        textAlign: 'right',
    },
});

interface LieferungItemProps {
    lieferung: Lieferung;
    lieferungPositionen: LieferungPosition[];
    withKunde?: boolean;
    viewMode: 'short' | 'long';
    withHeader?: boolean;
    editLieferung: () => void;
    // refetch: () => void;
}

export const LieferungItem: React.FC<LieferungItemProps> = ({
    lieferung,
    lieferungPositionen,
    withKunde,
    viewMode,
    withHeader = true,
    editLieferung,
}) => {
    const { kunde } = lieferung;

    const auth = useSelector(authSelector);
    const betreuerByNr = useSelector(betreuerByNrSelector);
    const textbausteineByKzNr = useSelector(textbausteineByKzNrSelector);
    const skontodefinitionenByArt = useSelector(skontodefinitionenByArtSelector);
    const laenderByIkz2 = useSelector(laenderByIkz2Selector);

    const history = useHistory();

    if (!kunde) {
        return null;
    }

    const renderTimestamps = () => (
        <Timestamps
            createdDate={lieferung.anlageDat}
            createdDecimalTime={lieferung.anlageUhr}
            createdUser={lieferung.anlageUser}
            modifiedDate={lieferung.updDat}
            modifiedDecimalTime={lieferung.updUhr}
            modifiedUser={lieferung.updUser}
        />
    );

    const renderStatus = () => <div className={styles.status}>{LieferungStatusLabel[lieferung.status]}</div>;

    const renderEditButton = () => (
        <div>
            {(auth.rolle === 'GF' || auth.rolle === 'ID') && (
                <PrimaryButton
                    className={viewMode === 'short' ? formStyles.smallButton : ''}
                    onClick={() => {
                        editLieferung();
                    }}
                    text="Bearbeiten"
                />
            )}
        </div>
    );

    const containerClassName = classnames(viewMode === 'short' ? formStyles.listItemShort : formStyles.listItem, {
        [formStyles.lieferungInArbeit]: lieferung.status === 0 || lieferung.status === 9,
        [formStyles.lieferungVersendet]: lieferung.status === 1 || lieferung.status === 7 || lieferung.status === 13,
        [formStyles.lieferungAbgeschlossen]: lieferung.status === 4,
    });

    if (viewMode === 'short') {
        return (
            <div className={containerClassName}>
                <div className={styles.shortLine}>
                    <div className={formStyles.shortValue}>
                        <strong>{lieferung.nr}</strong>
                    </div>
                    <div className={formStyles.shortValue}>
                        <strong>{formatSmDate(lieferung.datum)}</strong>
                    </div>

                    {withKunde ? (
                        <div
                            className={classnames(formStyles.listTitleShort, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                            }}
                        >
                            {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                        </div>
                    ) : (
                        <div />
                    )}

                    {renderStatus()}
                    {renderEditButton()}
                </div>

                <div>
                    <LieferungPositionen
                        kunde={kunde}
                        lieferung={lieferung}
                        lieferungPositionen={lieferungPositionen}
                        viewMode={viewMode}
                        withHeader={withHeader}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={containerClassName}>
                <div className={formStyles.topBlock}>
                    <div>
                        <div
                            className={classnames(formStyles.listTitle, formStyles.clickable)}
                            onClick={() => {
                                history.push('/kunden/' + kunde.nr);
                                // openDetails();
                            }}
                        >
                            Lieferung {lieferung.nr} - {`${kunde.name1} ${kunde.name2} (${kunde.nr})`}
                        </div>
                    </div>
                    {renderStatus()}
                    {renderEditButton()}
                </div>
                <div className={formStyles.fourColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.line}>
                            {kunde.plz} {kunde.ort}, {kunde.strasse} {kunde.land && `(${kunde.land})`}
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ansprechpartner:</div>
                            <div className={formStyles.dataValue}>{lieferung.kaName}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferschein-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{lieferung.nr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag-Nr:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{lieferung.auNr}</strong>
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Rahmenlieferung:</div>
                            <div className={formStyles.dataValue}>{lieferung.rahmen ? 'Ja' : 'Nein'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Ausstellungsdat:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(lieferung.datum)}</strong>
                            </div>
                        </div>
                        {/*
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Auftrag gültig bis:</div>
                            <div className={formStyles.dataValue}>
                                <strong>{formatSmDate(lieferung.datBis)}</strong>
                            </div>
                        </div>
                        */}
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Betreuer:</div>
                            <div className={formStyles.dataValue}>
                                <strong>
                                    {lieferung.betreuer &&
                                        (betreuerByNr[lieferung.betreuer]
                                            ? betreuerByNr[lieferung.betreuer].name || lieferung.betreuer
                                            : lieferung.betreuer)}
                                </strong>
                            </div>
                        </div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Text:</div>
                            <div className={formStyles.dataValue}>{nl2br(lieferung.txt)}</div>
                        </div>
                        {/*
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 1:</div>
                            <div className={formStyles.dataValue}>
                                {lieferung.txtB1
                                    ? textbausteineByKzNr[24][lieferung.txtB1]
                                        ? textbausteineByKzNr[24][lieferung.txtB1].name || lieferung.txtB1
                                        : lieferung.txtB1
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Textbaustein 2:</div>
                            <div className={formStyles.dataValue}>
                                {lieferung.txtB2
                                    ? textbausteineByKzNr[24][lieferung.txtB2]
                                        ? textbausteineByKzNr[24][lieferung.txtB2].name || lieferung.txtB2
                                        : lieferung.txtB2
                                    : '-'}
                            </div>
                        </div>
                        */}
                        {lieferung.lieferAdr && (
                            <div className={formStyles.keyValueLine}>
                                <div className={formStyles.dataKey120}>Lieferadresse:</div>
                                <div className={formStyles.dataValue}>
                                    {`${lieferung.lieferAdr.name1} ${lieferung.lieferAdr.name2}`}
                                    <br />
                                    {lieferung.lieferAdr.plz} {lieferung.lieferAdr.ort}
                                    {', '}
                                    {lieferung.lieferAdr.strasse}
                                    {lieferung.lieferAdr.land &&
                                        ', ' +
                                            (lieferung.lieferAdr.land
                                                ? laenderByIkz2[lieferung.lieferAdr.land]?.text
                                                : '')}
                                </div>
                            </div>
                        )}
                        {lieferung.lieferKunde && (
                            <div className={formStyles.keyValueLine}>
                                <div className={formStyles.dataKey120}>Lieferkunde:</div>
                                <div className={formStyles.dataValue}>
                                    {lieferung.lieferKunde.name1} {lieferung.lieferKunde.name2}
                                </div>
                            </div>
                        )}
                        {lieferung.rechnungsAdr && (
                            <div className={formStyles.keyValueLine}>
                                <div className={formStyles.dataKey120}>Rechnungsadresse:</div>
                                <div className={formStyles.dataValue}>
                                    {`${lieferung.rechnungsAdr.name1} ${lieferung.rechnungsAdr.name2}`}
                                    <br />
                                    {lieferung.rechnungsAdr.plz} {lieferung.rechnungsAdr.ort}
                                    {', '}
                                    {lieferung.rechnungsAdr.strasse}
                                    {lieferung.rechnungsAdr.land &&
                                        ', ' +
                                            (lieferung.rechnungsAdr.land
                                                ? laenderByIkz2[lieferung.rechnungsAdr.land]?.text
                                                : '')}
                                </div>
                            </div>
                        )}
                        {lieferung.rechnungsKunde && (
                            <div className={formStyles.keyValueLine}>
                                <div className={formStyles.dataKey120}>Rechnungskunde:</div>
                                <div className={formStyles.dataValue}>
                                    {lieferung.rechnungsKunde.name1} {lieferung.rechnungsKunde.name2}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferbed.:</div>
                            <div className={formStyles.dataValue}>
                                {lieferung.liefBed
                                    ? textbausteineByKzNr[11][lieferung.liefBed]
                                        ? textbausteineByKzNr[11][lieferung.liefBed].name || lieferung.liefBed
                                        : lieferung.liefBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferzeit:</div>
                            <div className={formStyles.dataValue}>
                                {lieferung.liefZeit
                                    ? textbausteineByKzNr[14][lieferung.liefZeit]
                                        ? textbausteineByKzNr[14][lieferung.liefZeit].name || lieferung.liefZeit
                                        : lieferung.liefZeit
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Lieferdatum.:</div>
                            <div className={formStyles.dataValue}>
                                {lieferung.liefDat && (formatSmDate(lieferung.liefDat, true) || '-')}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Liefer-KW.:</div>
                            <div className={formStyles.dataValue}>{lieferung.liefKw ? lieferung.liefKw : '-'}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>Zahlungsbed.:</div>
                            <div className={formStyles.dataValue}>
                                {lieferung.zahlBed
                                    ? skontodefinitionenByArt[lieferung.zahlBed]
                                        ? skontodefinitionenByArt[lieferung.zahlBed].text || lieferung.zahlBed
                                        : lieferung.zahlBed
                                    : '-'}
                            </div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKey120}>MwSt:</div>
                            <div className={formStyles.dataValue}>{formatPercent(lieferung.mwstCode)}</div>
                        </div>
                    </div>
                    <div className={formStyles.fourthColumn}>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Bemerkungen (beim Kunden):</div>
                            <div className={formStyles.dataValue}>{nl2br(lieferung?.kunde?.bemerkung)}</div>
                        </div>
                        <div className={formStyles.keyValueLine}>
                            <div className={formStyles.dataKeyLine}>Lieferbemerkung (beim Kunden):</div>
                            <div className={formStyles.dataValue}>{nl2br(lieferung?.kunde?.liefBem)}</div>
                        </div>
                    </div>
                </div>
                <div>
                    <LieferungPositionen
                        kunde={kunde}
                        lieferung={lieferung}
                        lieferungPositionen={lieferungPositionen}
                        viewMode={viewMode}
                    />
                </div>
                <div className={formStyles.threeColumnsBlock}>
                    <div className={formStyles.firstColumn}>
                        <div className={formStyles.sumLine}>Summe Netto: {formatMoney(lieferung.sNetto)}</div>
                    </div>
                    <div className={formStyles.secondColumn}>
                        <div className={formStyles.sumLine}>Summe Brutto: {formatMoney(lieferung.sBrutto)}</div>
                    </div>
                    <div className={formStyles.thirdColumn}>
                        <div className={formStyles.sumLine}>Summe MwSt.: {formatMoney(lieferung.sMwst)}</div>
                    </div>
                </div>
                {renderTimestamps()}
            </div>
            {/* showCopy && (
                <KundeSelect
                    label="Kunde"
                    kNr={lieferung?.kNr}
                    name={kunde?.name1}
                    dialogOnly
                    onChange={async (newKNr) => {
                        if (lieferung && newKNr) {
                            await copyLieferung(newKNr, lieferung, lieferungPositionen);
                            refetch();
                            setShowCopy(false);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            ) */}
        </>
    );
};
